import React, { useEffect, useState } from "react";
import { IconButton, Stack, TableCell, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { HubButton, HubHookFormInput, HubIcon } from "../../components";
import { onUpdateDateHelper } from "./helper";
import { StyledHubHookFormDatePicker, StyledTableRow } from "./styles";

import LineItemContextMenu from "./LineItemContextMenu";
import OrderLineItemChip from "./OrderLineItemChip";
import { getDateData } from "../../helpers/dateHelper";
import { WarningUpdatingWorkOrder } from "../../dialogs";
import dayjs from "dayjs";

const OrderLineItemRow = ({
  idx,
  locationId,
  item = {},
  part = {},
  disabled = false,
  deletedValues = [],
  updatedItems = [],
  warnChange = false,
}) => {
  const { setValue, getValues, register } = useFormContext();

  const [deleted, setDeleted] = useState(deletedValues.includes(item.id));
  const [syncTrees, setSyncTrees] = useState(true);
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);

  const prefix = `${item.id}`;
  const existing = updatedItems.find((u) => u.id === item.id) || {};
  const rowItem = { ...item, ...existing };

  useEffect(() => {
    if (deleted) {
      const addedDeleted = [...new Set([...deletedValues, item.id])];
      setValue("deletedWorkOrders", addedDeleted);
    }
  }, [deleted]);

  useEffect(() => {
    register(prefix + ".trees", { required: true });
    register(prefix + ".patReq", { required: true });
  }, [register, prefix]);

  useEffect(() => {
    if (!syncTrees) return;

    // const ppt = part?.partsPerTrees[`${locationId}`];
    // const trees = Math.ceil(rowItem.quantity / ppt);
    // const patReq = trees * ppt;

    // setValue(prefix + ".trees", trees, { shouldDirty: false });
    setValue(prefix + ".patReq", rowItem.quantity, { shouldDirty: false });
  }, [part.id, rowItem.quantity, syncTrees]);

  const onFieldUpdate = () => {
    if (warnChange) setShowWarning(true);

    const preItem = getValues(prefix);
    let updatedItem = { ...item, ...preItem };

    const updatedIndex = updatedItems.findIndex((u) => u.id === item.id);
    updatedIndex > -1
      ? (updatedItems[updatedIndex] = updatedItem)
      : updatedItems.push(updatedItem);

    setValue("updatedWorkOrders", updatedItems);
  };

  const onUpdateQuantity = (quantity) => {
    const ppt = part?.partsPerTrees[`${locationId}`];

    if (syncTrees) {
      const trees = Math.ceil(quantity / ppt);
      const patReq = trees * ppt;
      setValue(prefix + ".quantity", patReq);
      setValue(prefix + ".trees", trees);
      setValue(prefix + ".patReq", patReq);
    } else {
      setValue(prefix + ".quantity", Number(quantity));
      setValue(prefix + ".patReq", Number(quantity));
    }
    onFieldUpdate();
  };

  const onUpdateEstimatedCompleted = (value) => {
    const qty = Number(getValues(prefix + ".quantity"));
    let next = Number(value);
    if (!Number.isFinite(next) || next < 0) next = 0;
    if (Number.isFinite(qty) && next > qty) next = qty;
    setValue(prefix + ".estimatedCompleted", next);
    onFieldUpdate();
  };

  const onUpdateTrees = (trees) => {
    const ppt = part?.partsPerTrees[`${locationId}`];

    if (syncTrees) {
      const patReq = trees * ppt;
      setValue(prefix + ".quantity", patReq);
      setValue(prefix + ".trees", trees);
      setValue(prefix + ".patReq", patReq);
    } else {
      setValue(prefix + ".trees", Number(trees));
    }
    onFieldUpdate();
  };

  const onUpdateDate = (value, isStart = false) => {
    const { startDate, dueDate, shipDate } = onUpdateDateHelper(
      value,
      isStart,
      part?.totalDays[`${locationId}`],
    );

    setValue(prefix + ".startDate", startDate);
    setValue(prefix + ".endDate", dueDate);
    setValue(prefix + ".shipDate", shipDate);
    onFieldUpdate();
  };

  const handleNav = () =>
    rowItem.woNumber && navigate(`/works/${rowItem.woNumber}`);

  const projectedDate = dayjs(rowItem?.projectedDate)
    .utc()
    .format("MM/DD/YYYY");

  if (deleted) return <></>;
  return (
    <>
      <StyledTableRow key={rowItem.id}>
        <TableCell disabled={disabled}>
          <OrderLineItemChip
            state={rowItem?.state}
            label={rowItem?.statusLabel}
          />
        </TableCell>
        <TableCell
          sx={{ minWidth: 100 }}
          variant="blueBold"
          disabled={disabled}
          onClick={handleNav}
        >
          <Typography variant="caption11" color="inherit">
            {rowItem?.woNumber}
          </Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 100 }} disabled={disabled}>
          {disabled ? (
            <Typography variant="caption11">{rowItem?.lotNumber}</Typography>
          ) : (
            <HubHookFormInput
              name={prefix + ".lotNumber"}
              placeholder="UT240000"
              defaultValue={rowItem?.lotNumber}
              onBlur={() => onFieldUpdate()}
              disableHelperText
              required
            />
          )}
        </TableCell>
        <TableCell
          sx={{ maxWidth: 75, width: 75, minWidth: 75 }}
          disabled={disabled}
        >
          {disabled ? (
            <Typography variant="caption11">{rowItem?.trees}</Typography>
          ) : (
            <HubHookFormInput
              name={prefix + ".trees"}
              placeholder="10"
              defaultValue={rowItem?.trees}
              onBlur={(e, value) => onUpdateTrees(value)}
              disableHelperText
              isNumber
              required
            />
          )}
        </TableCell>
        <TableCell sx={{ width: 20, minWidth: 20 }}>
          <HubButton
            icon={syncTrees ? "Lock" : "Unlock"}
            iconProps={{
              size: "12px",
            }}
            onClick={() => setSyncTrees(!syncTrees)}
            variant="contained"
            flow="white"
            size="xsmall"
            sx={{
              ":hover": {
                backgroundColor: "hubColors.white",
                // color: syncTrees
                //   ? "hubColors.greyDark"
                //   : "hubColors.mainFocusDark",
                svg: { strokeWidth: syncTrees ? 2.5 : 2.5 },
              },
              height: "100%",
              color: syncTrees ? "hubColors.grey" : "hubColors.mainFocus",
              svg: { strokeWidth: syncTrees ? 2 : 2.5 },
            }}
          />
        </TableCell>
        <TableCell
          sx={{ maxWidth: 75, width: 75, minWidth: 75 }}
          disabled={disabled}
        >
          {disabled ? (
            <Typography variant="caption11">{rowItem?.quantity}</Typography>
          ) : (
            <HubHookFormInput
              name={prefix + ".quantity"}
              placeholder="1000"
              defaultValue={rowItem?.quantity}
              onBlur={(e, value) => onUpdateQuantity(value)}
              disableHelperText
              isNumber
              required
            />
          )}
        </TableCell>
        <TableCell
          sx={{ maxWidth: 75, width: 75, minWidth: 75 }}
          disabled={disabled}
        >
          {disabled ? (
            <Typography variant="caption11">
              {rowItem?.estimatedCompleted ?? rowItem?.quantity}
            </Typography>
          ) : (
            <HubHookFormInput
              name={prefix + ".estimatedCompleted"}
              placeholder="1000"
              defaultValue={rowItem?.estimatedCompleted ?? rowItem?.quantity}
              onBlur={(e, value) => onUpdateEstimatedCompleted(value)}
              disableHelperText
              isNumber
              required
            />
          )}
        </TableCell>
        <TableCell sx={{ minWidth: 125 }} disabled={disabled}>
          {disabled ? (
            <Typography variant="caption11">
              {getDateData({
                startDate: rowItem?.startDate,
                includeYear: true,
              })}
            </Typography>
          ) : (
            <StyledHubHookFormDatePicker
              name={prefix + ".startDate"}
              placeholder="MM/DD/YYYY"
              defaultValue={rowItem?.startDate}
              onChange={(value) => onUpdateDate(value, 1)}
              disabled={!part?.id}
              required
            />
          )}
        </TableCell>
        <TableCell sx={{ minWidth: 125 }} disabled={disabled}>
          {disabled ? (
            <Typography variant="caption11">
              {getDateData({ startDate: rowItem?.endDate, includeYear: true })}
            </Typography>
          ) : (
            <StyledHubHookFormDatePicker
              name={prefix + ".endDate"}
              placeholder="MM/DD/YYYY"
              defaultValue={rowItem?.endDate}
              onChange={(value) => onUpdateDate(value)}
              disabled={!part?.id}
              required
            />
          )}
        </TableCell>
        <TableCell sx={{ minWidth: 125 }}>
          <Typography variant="caption11" color="text.secondary">
            {rowItem?.projectedDate ? projectedDate : "—"}
          </Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 125 }} disabled={disabled}>
          {disabled ? (
            <Typography variant="caption11">
              {getDateData({ startDate: rowItem?.shipDate, includeYear: true })}
            </Typography>
          ) : (
            <StyledHubHookFormDatePicker
              name={prefix + ".shipDate"}
              placeholder="MM/DD/YYYY"
              defaultValue={rowItem?.shipDate}
              onChange={onFieldUpdate}
              disabled={!part?.id}
              required
            />
          )}
        </TableCell>
        <TableCell sx={{ maxWidth: "35px" }}>
          <HubHookFormInput
            name={prefix + ".id"}
            defaultValue={rowItem?.id}
            disableHelperText
            hidden
          />
          <LineItemContextMenu
            id={idx}
            disabled={disabled}
            removeItem={() => setDeleted(true)}
          />
        </TableCell>
      </StyledTableRow>
      <WarningUpdatingWorkOrder
        onClose={() => setShowWarning(false)}
        isOpen={showWarning}
      />
    </>
  );
};

export default React.memo(OrderLineItemRow);
